import { UserDoc } from '@gooduncles/gu-app-schema';
import { chunk, uniqBy } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { combineLatest, map, of } from 'rxjs';

import { observeUsers } from 'src/utils/firebase/firebase-short-cut';

import { selectValidPartnersUserIds } from 'src/redux/slices/partners';
import { useAppSelector } from 'src/redux/store';

// Firestore in 쿼리는 최대 30개까지 지원
const FIRESTORE_IN_QUERY_LIMIT = 30;
const TEST_USER_EMAILS = ['appstore@gooduncles.com', 'google@gooduncles.com'];

export type StaffUserDoc = UserDoc & { partnersUser: boolean };

export interface AssigneeOption {
  userId: string;
  displayName: string;
  userName: string | null;
  email: string;
}

/**
 * 표시 형식: "이름(email@앞부분)"
 */
export function formatDisplayName(user: UserDoc | undefined): string {
  if (!user) return '-';
  const emailPrefix = user.email.split('@')[0];
  const userName = user.userName ?? '-';
  return `${userName}(${emailPrefix})`;
}

export interface UseStaffUsersOptions {
  activeOnly?: boolean;
}

/**
 * admin/manager 사용자와 파트너스 사용자를 조회하는 훅
 * - activeOnly: true면 active 상태 사용자만 필터링
 */
export const useStaffUsers = (options: UseStaffUsersOptions = {}) => {
  const { activeOnly = false } = options;
  const partnersUserIds = useAppSelector(selectValidPartnersUserIds);
  const [staffUsers, setStaffUsers] = useState<StaffUserDoc[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // 배열 참조 변경으로 인한 무한 루프 방지
  const partnersUserIdsKey = useMemo(() => partnersUserIds.join(','), [partnersUserIds]);

  useEffect(() => {
    setIsLoading(true);

    // role이 admin 또는 manager인 사용자 조회
    const usersObservable = observeUsers([['role', 'in', ['admin', 'manager']]]);

    // partnersUserIds를 30개씩 chunk로 나눠서 쿼리
    const ids = partnersUserIdsKey ? partnersUserIdsKey.split(',') : [];
    const chunkedIds = chunk(ids, FIRESTORE_IN_QUERY_LIMIT);
    const partnersUsersObservable =
      chunkedIds.length > 0
        ? combineLatest(chunkedIds.map((chunkIds) => observeUsers([['_id', 'in', chunkIds]]))).pipe(
            map((results) => results.flat()),
          )
        : of([] as UserDoc[]);

    const subscription = combineLatest([usersObservable, partnersUsersObservable]).subscribe({
      next: ([users, partnersUsers]) => {
        const partnersUserIdSet = new Set(partnersUsers.map((u) => u._id));
        const mergedUsers: StaffUserDoc[] = uniqBy([...users, ...partnersUsers], '_id')
          .map((user) => ({
            ...user,
            partnersUser: partnersUserIdSet.has(user._id),
          }))
          .filter((user) => !TEST_USER_EMAILS.includes(user.email))
          .filter((user) => !activeOnly || user.status === 'active');
        setStaffUsers(mergedUsers);
        setIsLoading(false);
      },
      error: (err) => {
        console.error('Failed to fetch staff users', err);
        setIsLoading(false);
      },
    });

    return () => subscription.unsubscribe();
  }, [partnersUserIdsKey, activeOnly]);

  // 담당자 선택 옵션 (AssigneeOption 형식)
  const assigneeOptions = useMemo<AssigneeOption[]>(
    () =>
      staffUsers.map((user) => {
        const isPartner = user.partnersUser && user.role !== 'admin' && user.role !== 'manager';
        const prefix = isPartner ? '🚚 ' : '';
        return {
          userId: user._id,
          displayName: `${prefix}${formatDisplayName(user)}`,
          userName: user.userName ?? null,
          email: user.email,
        };
      }),
    [staffUsers],
  );

  return { staffUsers, assigneeOptions, isLoading };
};
